import { Dispatch } from 'redux';
import actionCreatorFactory from 'typescript-fsa';
import values from 'lodash/values';
import uniqWith from 'lodash/uniqWith';
import { FlashMessage } from '@pegasus/ui-flashmessages';

import {
    FLASH_MESSAGE_TYPE_SUCCESS,
    FLASH_MESSAGE_TYPE_ERROR,
    FLASH_MESSAGE_TYPE_WARNING,
    SPECIAL_COUPON_ERROR_HEADER,
} from '@client/config/constants';
import {
    getFlashMessages as getFlashMessagesFromService,
    createFlashMessage as createFlashMessageFromService,
} from '../../../services/api/flashMessages';
import { NAMESPACE } from './constants';

const actionCreator = actionCreatorFactory(NAMESPACE);

export interface HeaderPayload extends FlashMessage {
    callbackText?: string;
    clickCallback?: () => void;
}

export interface NotificationPayload {
    // eslint-disable-next-line no-undef
    notification: JSX.Element;
}

export const showFlashMessage = actionCreator<FlashMessage>('SHOW_FLASH_MESSAGE');

export const hideFlashMessage = actionCreator('HIDE_FLASH_MESSAGE');

export const showHeaderFlashMessage = actionCreator<HeaderPayload>('SHOW_HEADER_FLASH_MESSAGE');

export const hideHeaderFlashMessage = actionCreator('HIDE_HEADER_FLASH_MESSAGE');

export const showHeaderSuccessFlashMessage = (message: string) =>
    showHeaderFlashMessage({
        type: FLASH_MESSAGE_TYPE_SUCCESS,
        message,
    });

export const showHeaderErrorFlashMessage = (message: string) =>
    showHeaderFlashMessage({
        type: FLASH_MESSAGE_TYPE_ERROR,
        message,
    });

export const showHeaderWarningFlashMessage = (message: string) =>
    showHeaderFlashMessage({
        type: FLASH_MESSAGE_TYPE_WARNING,
        message,
    });

export const showHeaderNotificationAction = actionCreator<NotificationPayload>(
    'SHOW_HEADER_NOTIFICATION',
);
export const hideHeaderNotificationAction = actionCreator('HIDE_HEADER_NOTIFICATION');

// eslint-disable-next-line no-undef
export const showHeaderNotificationBanner = (notification: JSX.Element) =>
    showHeaderNotificationAction({ notification });

export const hideHeaderNotificationBanner = () => hideHeaderNotificationAction();

export const showNotification = actionCreator('SHOW_NOTIFICATION');

export const hideNotification = actionCreator('HIDE_NOTIFICATION');

export const fetchingFlashMessages = actionCreator.async<void, FlashMessage, Error>(
    'FETCH_FLASH_MESSAGES',
);

export const fetchFlashMessages = () => (dispatch: Dispatch) => {
    dispatch(fetchingFlashMessages.started());

    return getFlashMessagesFromService()
        .then(result => {
            const messages = values(result);
            if (!messages.length) {
                return Promise.resolve([]);
            }

            const uniqMessages = uniqWith(
                messages,
                (a, b) =>
                    a.type === b.type &&
                    ((a.message && b.message && a.message === b.message) ||
                        (a.text && b.text && a.text === b.text)),
            );

            uniqMessages.forEach((message: FlashMessage) => {
                if (message.message === 'platform-journeys-success-page-notification') {
                    dispatch(showNotification());
                    return;
                }

                if (
                    message.type === FLASH_MESSAGE_TYPE_SUCCESS ||
                    message.message === SPECIAL_COUPON_ERROR_HEADER
                ) {
                    dispatch(showHeaderFlashMessage(message));

                    return;
                }

                dispatch(showFlashMessage(message));
            });

            return messages;
        })
        .catch(() => {
            return [];
        });
};

export const creatingFlashMessage = actionCreator.async<void, FlashMessage, Error>(
    'CREATE_FLASH_MESSAGES',
);

export const createFlashMessage = (flashMessage: FlashMessage) => (dispatch: Dispatch) => {
    dispatch(creatingFlashMessage.started());

    return createFlashMessageFromService(flashMessage)
        .then(messages => {
            dispatch(creatingFlashMessage.done({ result: messages }));

            return messages;
        })
        .catch(error => {
            dispatch(creatingFlashMessage.failed(error));

            return error;
        });
};
